import React from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import "../styles/domain-pages.css";

const energyCapabilities = [
  "Sub-metering and continuous utility interval data tracking",
  "Automated utility billing reconciliation vs. actual consumption",
  "Asset-level energy efficiency and peak-demand variance logs",
  "Asset maintenance schedules aligned to energy efficiency profiles",
  "Multi-fuel, raw electricity, and chemical emissions mapping models",
  "Auditable data pipelines built for NGER, Scope 1, 2, and 3 reporting",
  "Interactive Power BI energy operational performance dashboards",
  "Direct ingestion lines for procurement contracts and vendor metrics",
];

const energyOutcomes = [
  "End-to-end auditability across infrastructure assets, billing, and greenhouse gas metrics",
  "Drastic reduction in manual overhead by matching utility meter feeds to ERP records automatically",
  "Clear data lineage extending from field meters right up to executive ESG compliance disclosures",
  "Production-ready data models tailored for Power BI analytics, compliance, and engineering teams",
  "Rapid detection of operational anomalies, leakages, and contract pricing deviations across facilities",
  "A governed analytical platform ready to automate complex resource performance targets",
];

const energyEvidenceRows = [
  {
    source: "Interval meters",
    control: "Consumption validation",
    output: "Site usage facts",
  },
  {
    source: "Utility invoices",
    control: "ERP cost matching",
    output: "Billing variance log",
  },
  {
    source: "Emission factors",
    control: "Scope calculation rules",
    output: "Audit evidence pack",
  },
  {
    source: "Procurement contracts",
    control: "Tariff and vendor checks",
    output: "Price exception review",
  },
];

const energyAssetContextRows = [
  {
    source: "Asset hierarchy",
    control: "Site and meter ownership",
    output: "Accountable asset model",
  },
  {
    source: "Operating context",
    control: "Run-hour alignment",
    output: "Usage driver profile",
  },
  {
    source: "Maintenance windows",
    control: "Downtime annotation",
    output: "Efficiency variance log",
  },
  {
    source: "Production volumes",
    control: "Intensity calculations",
    output: "Operational benchmark",
  },
];

function EnergyEvidencePanel() {
  return (
    <div className="energy-panel energy-evidence-panel">
      <div className="energy-panel-header">
        <span>Evidence Map</span>
        <strong>Meter-to-ledger controls</strong>
      </div>
      <div className="energy-evidence-table">
        <div className="energy-evidence-head">
          <span>Source</span>
          <span>Control</span>
          <span>Output</span>
        </div>
        {energyEvidenceRows.map((row) => (
          <div className="energy-evidence-row" key={`${row.source}-${row.output}`}>
            <span>{row.source}</span>
            <span>{row.control}</span>
            <strong>{row.output}</strong>
          </div>
        ))}
      </div>
      <p>Each row becomes a governed data contract with ownership, validation logic and audit traceability.</p>
    </div>
  );
}

function EnergyRegisterPanel() {
  return (
    <div className="energy-panel energy-evidence-panel">
      <div className="energy-panel-header">
        <span>Operating Model</span>
        <strong>Asset-to-energy context</strong>
      </div>
      <div className="energy-evidence-table">
        <div className="energy-evidence-head">
          <span>Source</span>
          <span>Control</span>
          <span>Output</span>
        </div>
        {energyAssetContextRows.map((row) => (
          <div className="energy-evidence-row" key={`${row.source}-${row.output}`}>
            <span>{row.source}</span>
            <span>{row.control}</span>
            <strong>{row.output}</strong>
          </div>
        ))}
      </div>
      <p>Operational events become part of the same governed model used by engineering, finance and ESG teams.</p>
    </div>
  );
}

export default function EnergyData() {
  return (
    <div className="site-page">
      <Seo
        title="Energy Data Platforms & Infrastructure Engineering | Data Apparatus"
        description="Enterprise energy data engineering. We connect industrial sub-meters, billing contracts, production logs, and emission factors into governed Power BI analytics structures."
      />

      <section className="page-hero energy">
        <div className="page-hero-copy">
          <span className="eyebrow">Industrial Energy Foundations</span>
          <h1>Data platforms built to unify complex energy, asset, and emissions profiles.</h1>
          <p>
            Bridge the gap between raw meter streams, physical asset operations, supply chains, and
            corporate ledger sheets. We build the data architecture that drives industrial energy performance.
          </p>
          <div className="hero-actions">
            <Link to="/contact" className="primary-btn">
              Discuss energy data
            </Link>
            <Link to="/domain-expertise/sustainability-reporting" className="secondary-btn">
              View sustainability reporting
            </Link>
          </div>
        </div>
      </section>

      <section className="page-section">
        <div className="content-split">
          <div>
            <span className="eyebrow">The data friction</span>
            <h2>Industrial energy strategies stall when physical consumption data and core financial records do not match.</h2>
            <p>
              True energy efficiency requires blending utility interval streams, third-party vendor invoices, 
              machinery maintenance telemetry, and complex emission factor databases. Without a governed, 
              centralized data lake, reporting stays manual and performance opportunities remain hidden.
            </p>
          </div>
          <div className="capability-list">
            {energyCapabilities.map((capability) => (
              <div key={capability}>{capability}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="feature-block-section">
        <div className="feature-block">
          <div className="feature-copy">
            <span className="eyebrow">01 / Meter-to-ledger auditability</span>
            <h2>Make every kilowatt and emission metric fully traceable from source to sign-off.</h2>
            <p>
              We integrate distributed meter infrastructure directly with your procurement networks and 
              ERP accounts. The result is a single, bulletproof dataset optimized for real-time 
              variance tracking and auditable carbon bookkeeping.
            </p>
          </div>
          <EnergyEvidencePanel />
        </div>

        <div className="feature-block reverse">
          <div className="feature-copy">
            <span className="eyebrow">02 / Contextual asset engineering</span>
            <h2>Overlay energy profiles with real-world operational and maintenance timelines.</h2>
            <p>
              Energy usage doesn't happen in a vacuum. Our data engineering structures tie consumption patterns 
              directly to site output volumes, asset run-times, and mechanical reliability lifecycles so your 
              operations teams can locate and fix the true root causes of cost overruns.
            </p>
          </div>
          <EnergyRegisterPanel />
        </div>
      </section>

      <section className="page-section alt">
        <div className="section-heading">
          <span className="eyebrow">Strategic Value</span>
          <h2>Clear operational capabilities for energy and facilities teams.</h2>
        </div>
        <div className="outcome-grid">
          {energyOutcomes.map((outcome) => (
            <div className="outcome-card" key={outcome}>
              {outcome}
            </div>
          ))}
        </div>
      </section>

      <section className="cta-band">
        <h2>Ready to build a reliable energy data foundation?</h2>
        <p>Let's map your existing meter setups, vendor files, core systems, and target analytical use cases.</p>
        <Link to="/contact" className="primary-btn">
          Book an energy data review
        </Link>
      </section>

      <Footer />
    </div>
  );
}
